import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../../_metronic/_partials/controls";
import * as categoriesCrud from "../../_redux/categories/categoriesCrud";
import * as businessesCrud from "../../_redux/businesses/businessesCrud";

const API_BASE = process.env.REACT_APP_API_ENDPOINT + "/advertisement";

export function AdvertisementsPage() {
  const { user } = useSelector((state) => state.auth);
  const userobject = user?.userData || {};
  let superadmin = true;
  if (
    userobject.admin_role &&
    userobject.admin_role !== "superadmin" &&
    userobject.is_admin
  ) {
    superadmin = false;
  }

  // Master Data
  const [categories, setCategories] = useState([]);
  const [categoriesWithBusinesses, setCategoriesWithBusinesses] = useState([]);
  const [businesses, setBusinesses] = useState([]);

  // Top-Level Tab State: 'boxes' | 'all-ads'
  const [activeTab, setActiveTab] = useState("boxes");

  // Selected Box for Drilldown (null = show all boxes)
  const [selectedBox, setSelectedBox] = useState(null);

  // Boxes State
  const [boxes, setBoxes] = useState([]);
  const [boxesLoading, setBoxesLoading] = useState(false);

  // Advertisements State
  const [ads, setAds] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [adsLoading, setAdsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderSaveMessage, setOrderSaveMessage] = useState("");

  // Drag and Drop States (For both Table view and Box-to-Box Kanban view)
  const [draggedAdData, setDraggedAdData] = useState(null); // { adId, sourceBoxId, adIndex }
  const [dragOverBoxId, setDragOverBoxId] = useState(null);
  const [dragOverAdIndex, setDragOverAdIndex] = useState(null);

  // Query / Filter State for Ads Table
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchDetail, setSearchDetail] = useState("");
  const [filterBox, setFilterBox] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterBusiness, setFilterBusiness] = useState("");
  const [filterMediaType, setFilterMediaType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDefault, setFilterDefault] = useState("");

  // Box Modal State
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [currentBox, setCurrentBox] = useState({
    id: null,
    box_number: "",
    name: "",
    description: "",
    status: "active",
    order: 1,
  });
  const [boxModalErrors, setBoxModalErrors] = useState({});
  const [savingBox, setSavingBox] = useState(false);

  // Ad Modal State
  const [showAdModal, setShowAdModal] = useState(false);
  const [currentAd, setCurrentAd] = useState({
    id: null,
    title: "",
    detail: "",
    action: "",
    thumb: "",
    mediatype: "eAdvertText",
    status: "active",
    box_id: "",
    ad_order: 1,
    category_id: "",
    business_id: "",
    is_default: false,
  });
  const [adModalErrors, setAdModalErrors] = useState({});
  const [savingAd, setSavingAd] = useState(false);
  const [uploadingAction, setUploadingAction] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);

  // Fetch Categories & Businesses on Mount
  useEffect(() => {
    categoriesCrud
      .getAllCategories()
      .then((res) => {
        if (res.data && res.data.data) {
          setCategories(res.data.data);
        } else if (Array.isArray(res.data)) {
          setCategories(res.data);
        }
      })
      .catch((err) => console.error("Error fetching categories:", err));

    axios
      .post(`${API_BASE}/categories-with-businesses`, {})
      .then((res) => {
        if (res.data && res.data.success && Array.isArray(res.data.categories)) {
          setCategoriesWithBusinesses(res.data.categories);
        }
      })
      .catch((err) =>
        console.error("Error fetching categories with businesses:", err)
      );

    businessesCrud
      .findBusinesses({
        pageNumber: 1,
        pageSize: 1000,
        sortField: "name",
        sortOrder: "asc",
        filter: {},
      })
      .then((res) => {
        if (res.data && Array.isArray(res.data.entities)) {
          setBusinesses(res.data.entities);
        } else if (res.data && Array.isArray(res.data.data)) {
          setBusinesses(res.data.data);
        } else if (Array.isArray(res.data)) {
          setBusinesses(res.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching businesses:", err);
        businessesCrud
          .getAllBusinesses()
          .then((fallbackRes) => {
            const list =
              fallbackRes.data?.entities ||
              fallbackRes.data?.data ||
              (Array.isArray(fallbackRes.data) ? fallbackRes.data : []);
            if (list.length > 0) setBusinesses(list);
          })
          .catch((e) => console.error("Fallback businesses fetch failed:", e));
      });
  }, []);

  // Helper to extract all category IDs associated with a business
  const extractBusinessCategoryIds = (b) => {
    if (!b) return [];
    const ids = new Set();

    if (b.category_id) {
      if (Array.isArray(b.category_id)) {
        b.category_id.forEach((id) => {
          const n = Number(String(id).replace(/[\[\]]/g, "").trim());
          if (!isNaN(n) && n > 0) ids.add(n);
        });
      } else {
        String(b.category_id)
          .replace(/[\[\]]/g, "")
          .split(",")
          .forEach((s) => {
            const n = Number(s.trim());
            if (!isNaN(n) && n > 0) ids.add(n);
          });
      }
    }

    if (Array.isArray(b.user_sub_categories)) {
      b.user_sub_categories.forEach((usc) => {
        if (usc.sub_category && usc.sub_category.category_id) {
          const n = Number(usc.sub_category.category_id);
          if (!isNaN(n) && n > 0) ids.add(n);
        }
      });
    }

    return Array.from(ids);
  };

  // Check if a business belongs to a given category ID
  const businessBelongsToCategory = (b, catId) => {
    if (!catId) return true;
    const targetId = Number(catId);
    if (!targetId) return true;
    const bizCatIds = extractBusinessCategoryIds(b);
    return bizCatIds.includes(targetId);
  };

  // Compute Categories that have associated Businesses
  const categoriesWithBizList = useMemo(() => {
    const catMap = new Map();

    // 1. Add categories returned from backend query
    categoriesWithBusinesses.forEach((c) => {
      catMap.set(c.id, c);
    });

    // 2. Also check loaded businesses for any category references
    businesses.forEach((b) => {
      const catIds = extractBusinessCategoryIds(b);
      catIds.forEach((id) => {
        if (!catMap.has(id)) {
          const matched = categories.find((c) => c.id === id);
          if (matched) catMap.set(id, matched);
        }
      });
    });

    return Array.from(catMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [categoriesWithBusinesses, businesses, categories]);

  // Fetch Boxes (Top-Level Categories)
  const fetchBoxes = async () => {
    setBoxesLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/boxes/find`, {});
      if (response.data && response.data.success) {
        const boxData = response.data.boxes || [];
        setBoxes(boxData);
        if (selectedBox) {
          const updatedSelected = boxData.find((b) => b.id === selectedBox.id);
          if (updatedSelected) {
            setSelectedBox(updatedSelected);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching advertisement boxes:", error);
    } finally {
      setBoxesLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Advertisements for Table View
  const fetchAds = async () => {
    setAdsLoading(true);
    try {
      const boxFilterVal = selectedBox ? selectedBox.id : filterBox || undefined;
      const response = await axios.post(`${API_BASE}/find`, {
        queryParams: {
          pageNumber,
          pageSize,
          sortField: "ad_order",
          sortOrder: "asc",
          filter: {
            title: searchTitle,
            detail: searchDetail,
            box_id: boxFilterVal,
            category_id: filterCategory || undefined,
            business_id: filterBusiness || undefined,
            mediatype: filterMediaType,
            status: filterStatus,
            is_default: filterDefault || undefined,
          },
        },
      });
      if (response.data && response.data.success) {
        setAds(response.data.entities || []);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching advertisements:", error);
    } finally {
      setAdsLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNumber,
    pageSize,
    selectedBox,
    filterBox,
    filterCategory,
    filterBusiness,
    filterMediaType,
    filterStatus,
    filterDefault,
  ]);

  // Handle Box Select / Drilldown
  const handleSelectBox = (box) => {
    setSelectedBox(box);
    setPageNumber(1);
    setSearchTitle("");
    setSearchDetail("");
    setFilterBox("");
  };

  const handleBackToBoxes = () => {
    setSelectedBox(null);
    setPageNumber(1);
    fetchBoxes();
  };

  // -------------------------------------------------------------
  // CROSS-BOX DRAG AND DROP HANDLERS
  // -------------------------------------------------------------
  const handleAdDragStart = (e, ad, sourceBoxId, adIndex) => {
    const dragData = {
      adId: ad.id,
      sourceBoxId: sourceBoxId,
      adIndex: adIndex,
    };
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
    e.dataTransfer.setData("text/plain", String(ad.id));
    e.dataTransfer.effectAllowed = "move";
    setDraggedAdData(dragData);
  };

  const handleAdDragEnd = () => {
    setDraggedAdData(null);
    setDragOverBoxId(null);
    setDragOverAdIndex(null);
  };

  const handleBoxDragOver = (e, boxId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverBoxId !== boxId) {
      setDragOverBoxId(boxId);
    }
  };

  const handleBoxDragLeave = (e, boxId) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverBoxId === boxId) {
      setDragOverBoxId(null);
    }
  };

  // Drop an ad onto a Box Card
  const handleDropOnBox = async (e, targetBox) => {
    e.preventDefault();
    setDragOverBoxId(null);

    let droppedData = draggedAdData;
    if (!droppedData) {
      try {
        const jsonStr = e.dataTransfer.getData("application/json");
        if (jsonStr) droppedData = JSON.parse(jsonStr);
      } catch (err) {}
    }

    if (!droppedData || !droppedData.adId) return;

    const adId = Number(droppedData.adId);
    const sourceBoxId = droppedData.sourceBoxId;

    if (sourceBoxId === targetBox.id) {
      return; // Already in this box
    }

    // Optimistically update local state
    setBoxes((prevBoxes) => {
      let movedAd = null;
      const nextBoxes = prevBoxes.map((b) => {
        if (b.id === sourceBoxId) {
          const filteredAds = (b.advertisements || []).filter((a) => {
            if (a.id === adId) {
              movedAd = a;
              return false;
            }
            return true;
          });
          return {
            ...b,
            advertisements: filteredAds,
            totalAds: filteredAds.length,
            activeAds: filteredAds.filter((a) => a.status === "active").length,
            inactiveAds: filteredAds.filter((a) => a.status !== "active").length,
          };
        }
        return b;
      });

      if (movedAd) {
        return nextBoxes.map((b) => {
          if (b.id === targetBox.id) {
            const updatedAd = {
              ...movedAd,
              box_id: targetBox.id,
              ad_order: targetBox.box_number,
              box: { id: targetBox.id, box_number: targetBox.box_number, name: targetBox.name },
            };
            const updatedAds = [...(b.advertisements || []), updatedAd];
            return {
              ...b,
              advertisements: updatedAds,
              totalAds: updatedAds.length,
              activeAds: updatedAds.filter((a) => a.status === "active").length,
              inactiveAds: updatedAds.filter((a) => a.status !== "active").length,
            };
          }
          return b;
        });
      }
      return prevBoxes;
    });

    setSavingOrder(true);
    setOrderSaveMessage(`Moving ad to Box ${targetBox.box_number}...`);

    try {
      await axios.post(`${API_BASE}/reorder`, {
        items: [
          {
            id: adId,
            box_id: targetBox.id,
            ad_order: targetBox.box_number,
          },
        ],
      });
      setOrderSaveMessage(`Successfully moved ad to Box ${targetBox.box_number}!`);
      setTimeout(() => setOrderSaveMessage(""), 3500);
      fetchBoxes();
      if (selectedBox) fetchAds();
    } catch (err) {
      console.error("Error moving ad between boxes:", err);
      alert("Failed to move advertisement. Reverting changes.");
      fetchBoxes();
    } finally {
      setSavingOrder(false);
      setDraggedAdData(null);
    }
  };

  // Re-ordering logic in Table View
  const handleTableMove = async (fromIndex, toIndex) => {
    if (
      fromIndex < 0 ||
      fromIndex >= ads.length ||
      toIndex < 0 ||
      toIndex >= ads.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    const updatedAds = Array.from(ads);
    const [movedItem] = updatedAds.splice(fromIndex, 1);
    updatedAds.splice(toIndex, 0, movedItem);

    const reorderedWithNumbers = updatedAds.map((ad, idx) => ({
      ...ad,
      ad_order: idx + 1 + (pageNumber - 1) * pageSize,
    }));

    setAds(reorderedWithNumbers);
    setSavingOrder(true);
    setOrderSaveMessage("");

    try {
      const items = reorderedWithNumbers.map((ad) => ({
        id: ad.id,
        ad_order: ad.ad_order,
        box_id: ad.box_id,
      }));
      await axios.post(`${API_BASE}/reorder`, { items });
      setOrderSaveMessage("Order saved!");
      setTimeout(() => {
        setOrderSaveMessage("");
      }, 3000);
      fetchBoxes();
    } catch (error) {
      console.error("Error saving advertisements order:", error);
      alert("Failed to save re-ordered advertisements. Reverting changes.");
      fetchAds();
    } finally {
      setSavingOrder(false);
    }
  };

  // Checkbox Selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(ads.map((ad) => ad.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    }
  };

  // Toggle Ad Status
  const toggleAdStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.post(`${API_BASE}/updateStatusForAdvertisements`, {
        ids: [id],
        status: newStatus,
      });
      fetchAds();
      fetchBoxes();
    } catch (error) {
      console.error("Error toggling ad status:", error);
    }
  };

  // Toggle Default Ad Status
  const toggleAdDefault = async (id, currentDefault) => {
    try {
      await axios.post(`${API_BASE}/toggleDefault`, {
        id: id,
        is_default: !currentDefault,
      });
      fetchAds();
      fetchBoxes();
    } catch (error) {
      console.error("Error toggling ad default:", error);
    }
  };

  // Toggle Box Status
  const toggleBoxStatus = async (boxId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.post(`${API_BASE}/boxes/updateStatus`, {
        id: boxId,
        status: newStatus,
      });
      fetchBoxes();
    } catch (error) {
      console.error("Error toggling box status:", error);
    }
  };

  // Delete individual Ad
  const handleDeleteAd = async (id) => {
    if (window.confirm("Are you sure you want to delete this advertisement?")) {
      try {
        await axios.post(`${API_BASE}/delete`, { id });
        setSelectedIds(selectedIds.filter((x) => x !== id));
        fetchAds();
        fetchBoxes();
      } catch (error) {
        console.error("Error deleting advertisement:", error);
      }
    }
  };

  // Delete Box
  const handleDeleteBox = async (box) => {
    const confirmMsg =
      box.totalAds > 0
        ? `Box "${box.name}" contains ${box.totalAds} advertisement(s).\n\nAre you sure you want to delete this Box? (Its advertisements will be moved to Box 1)`
        : `Are you sure you want to delete Box "${box.name}"?`;

    if (window.confirm(confirmMsg)) {
      try {
        await axios.post(`${API_BASE}/boxes/delete`, { id: box.id });
        if (selectedBox && selectedBox.id === box.id) {
          setSelectedBox(null);
        }
        fetchBoxes();
      } catch (error) {
        console.error("Error deleting box:", error);
        alert(
          "Failed to delete box: " +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  // Bulk Delete Ads
  const handleBulkDeleteAds = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedIds.length} selected advertisements?`
      )
    ) {
      try {
        await axios.post(`${API_BASE}/deleteAdvertisements`, {
          ids: selectedIds,
        });
        setSelectedIds([]);
        fetchAds();
        fetchBoxes();
      } catch (error) {
        console.error("Error bulk deleting ads:", error);
      }
    }
  };

  // Bulk Status Change
  const handleBulkStatusAds = async (status) => {
    try {
      await axios.post(`${API_BASE}/updateStatusForAdvertisements`, {
        ids: selectedIds,
        status: status,
      });
      setSelectedIds([]);
      fetchAds();
      fetchBoxes();
    } catch (error) {
      console.error("Error bulk updating status:", error);
    }
  };

  // Bulk Assign to Box
  const handleBulkAssignBox = async () => {
    if (boxes.length === 0) {
      alert("No boxes available.");
      return;
    }

    const boxPrompt = window.prompt(
      `Enter the Box Number to move ${selectedIds.length} ad(s) to:\nAvailable Boxes: ${boxes
        .map((b) => `Box ${b.box_number} (${b.name})`)
        .join(", ")}`
    );
    if (!boxPrompt) return;

    const boxNum = parseInt(boxPrompt, 10);
    const targetBox = boxes.find((b) => b.box_number === boxNum);
    if (!targetBox) {
      alert(`Box ${boxNum} not found. Please enter a valid Box number.`);
      return;
    }

    try {
      const items = selectedIds.map((id) => ({
        id,
        box_id: targetBox.id,
        ad_order: targetBox.box_number,
      }));
      await axios.post(`${API_BASE}/reorder`, { items });
      setSelectedIds([]);
      fetchAds();
      fetchBoxes();
    } catch (error) {
      console.error("Error moving ads to box:", error);
      alert("Failed to assign box.");
    }
  };

  // Open Box Modal
  const handleOpenAddBoxModal = () => {
    const nextBoxNumber =
      boxes.length > 0
        ? Math.max(...boxes.map((b) => b.box_number || 0)) + 1
        : 1;

    setCurrentBox({
      id: null,
      box_number: nextBoxNumber,
      name: `Box ${nextBoxNumber}`,
      description: "",
      status: "active",
      order: nextBoxNumber,
    });
    setBoxModalErrors({});
    setShowBoxModal(true);
  };

  const handleOpenEditBoxModal = (box) => {
    setCurrentBox({
      id: box.id,
      box_number: box.box_number,
      name: box.name || "",
      description: box.description || "",
      status: box.status || "active",
      order: box.order || box.box_number,
    });
    setBoxModalErrors({});
    setShowBoxModal(true);
  };

  // Save Box
  const handleSaveBox = async () => {
    const errors = {};
    if (!currentBox.name || currentBox.name.trim() === "") {
      errors.name = "Box name is required.";
    }
    if (
      !currentBox.box_number ||
      isNaN(parseInt(currentBox.box_number, 10)) ||
      parseInt(currentBox.box_number, 10) <= 0
    ) {
      errors.box_number = "A valid positive box number is required.";
    }

    if (Object.keys(errors).length > 0) {
      setBoxModalErrors(errors);
      return;
    }

    setSavingBox(true);
    try {
      const payload = {
        box_number: parseInt(currentBox.box_number, 10),
        name: currentBox.name.trim(),
        description: currentBox.description,
        status: currentBox.status,
        order:
          parseInt(currentBox.order, 10) ||
          parseInt(currentBox.box_number, 10),
      };

      if (currentBox.id) {
        await axios.post(`${API_BASE}/boxes/update/${currentBox.id}`, payload);
      } else {
        await axios.post(`${API_BASE}/boxes/create`, payload);
      }

      setShowBoxModal(false);
      fetchBoxes();
    } catch (error) {
      console.error("Error saving box:", error);
      alert(error.response?.data?.message || "Failed to save Box.");
    } finally {
      setSavingBox(false);
    }
  };

  // Open Ad Modal
  const handleOpenAddAdModal = (defaultBoxId = null) => {
    let initialBoxId = defaultBoxId || (selectedBox ? selectedBox.id : "");
    if (!initialBoxId && boxes.length > 0) {
      initialBoxId = boxes[0].id;
    }

    const assignedBox = boxes.find((b) => b.id === initialBoxId);

    setCurrentAd({
      id: null,
      title: "",
      detail: "",
      action: "",
      thumb: "",
      mediatype: "eAdvertText",
      status: "active",
      box_id: initialBoxId,
      ad_order: assignedBox ? assignedBox.box_number : 1,
      category_id: "",
      business_id: "",
      is_default: false,
    });
    setAdModalErrors({});
    setShowAdModal(true);
  };

  const handleOpenEditAdModal = (ad) => {
    setCurrentAd({
      id: ad.id,
      title: ad.title || "",
      detail: ad.detail || "",
      action: ad.action || "",
      thumb: ad.thumb || "",
      mediatype: ad.mediatype || "eAdvertText",
      status: ad.status || "active",
      box_id: ad.box_id || (ad.box ? ad.box.id : "") || "",
      ad_order: ad.ad_order || 1,
      category_id: ad.category_id || "",
      business_id: ad.business_id || "",
      is_default: Boolean(ad.is_default),
    });
    setAdModalErrors({});
    setShowAdModal(true);
  };

  // S3 Upload Helper (Direct Server-side S3 upload: 100% immune to browser CORS, ETag checks, supports up to 100MB)
  const uploadToS3 = async (file) => {
    const mimeType =
      file.type ||
      (file.name?.endsWith(".mp4")
        ? "video/mp4"
        : file.name?.endsWith(".mov")
        ? "video/quicktime"
        : file.name?.endsWith(".webm")
        ? "video/webm"
        : file.name?.endsWith(".png")
        ? "image/png"
        : file.name?.endsWith(".gif")
        ? "image/gif"
        : "image/jpeg");

    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

      const response = await axios.post(
        `${API_BASE}/upload`,
        {
          file: base64Data,
          filename: file.name,
          mimeType: mimeType,
        },
        {
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        }
      );

      if (response.data && response.data.success && response.data.url) {
        return response.data.url;
      } else {
        throw new Error(response.data?.message || "Upload failed");
      }
    } catch (serverUploadError) {
      console.error("Server S3 upload failed:", serverUploadError);
      throw new Error(
        serverUploadError.response?.data?.message ||
          serverUploadError.message ||
          "Failed to upload media file to S3"
      );
    }
  };

  const handleActionFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAction(true);
    try {
      const url = await uploadToS3(file);
      let detectedMediaType = currentAd.mediatype;
      if (file.type.startsWith("video/")) {
        detectedMediaType = "eAdvertVideo";
      } else if (file.type === "image/gif") {
        detectedMediaType = "eAdvertGif";
      } else if (file.type.startsWith("image/")) {
        detectedMediaType = "eAdvertImage";
      }

      setCurrentAd({
        ...currentAd,
        action: url,
        mediatype: detectedMediaType,
      });
      e.target.value = "";
    } catch (err) {
      alert("Failed to upload file to S3: " + err.message);
    } finally {
      setUploadingAction(false);
    }
  };

  const handleThumbFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingThumb(true);
    try {
      const url = await uploadToS3(file);
      setCurrentAd({
        ...currentAd,
        thumb: url,
      });
      e.target.value = "";
    } catch (err) {
      alert("Failed to upload file to S3: " + err.message);
    } finally {
      setUploadingThumb(false);
    }
  };

  // Save Ad
  const handleSaveAd = async () => {
    const errors = {};
    if (!currentAd.box_id) {
      errors.box_id = "Please select a Box for this advertisement.";
    }
    if (!currentAd.action && currentAd.mediatype !== "eAdvertText") {
      errors.action = "Action (Media URL) is required for media advertisements.";
    }
    if (currentAd.mediatype === "eAdvertText" && !currentAd.detail) {
      errors.detail = "Detail text is required for text advertisements.";
    }

    if (Object.keys(errors).length > 0) {
      setAdModalErrors(errors);
      return;
    }

    setSavingAd(true);
    try {
      const assignedBox = boxes.find((b) => b.id === Number(currentAd.box_id));
      const payload = {
        ...currentAd,
        box_id: currentAd.box_id ? parseInt(currentAd.box_id, 10) : null,
        ad_order: assignedBox ? assignedBox.box_number : 1,
        category_id: currentAd.category_id
          ? parseInt(currentAd.category_id, 10)
          : null,
        business_id: currentAd.business_id
          ? parseInt(currentAd.business_id, 10)
          : null,
        is_default: Boolean(currentAd.is_default),
      };

      if (currentAd.id) {
        await axios.post(`${API_BASE}/update/${currentAd.id}`, {
          advertisement: payload,
        });
      } else {
        await axios.post(`${API_BASE}/create`, payload);
      }

      setShowAdModal(false);
      fetchAds();
      fetchBoxes();
    } catch (error) {
      console.error("Error saving advertisement:", error);
      alert(
        error.response?.data?.message || "Failed to save advertisement."
      );
    } finally {
      setSavingAd(false);
    }
  };

  const getProxyUrl = (url) => {
    if (url && url.includes("/adverts/")) {
      const filename = url.split("/adverts/").pop();
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const baseApi = apiEndpoint.replace("/admin", "");
      return `${baseApi}/media/render/${filename}`;
    }
    return url;
  };

  // Media preview helper
  const renderMediaPreview = (ad, compact = false) => {
    const width = compact ? "44px" : "60px";
    const height = compact ? "32px" : "40px";
    const style = {
      width,
      height,
      objectFit: "cover",
      borderRadius: "4px",
      border: "1px solid #ddd",
    };

    const handleImageError = (e) => {
      e.target.style.display = "none";
      const sibling = e.target.nextSibling;
      if (sibling) sibling.style.display = "flex";
    };

    let previewElement;

    if (ad.mediatype === "eAdvertVideo") {
      previewElement = ad.thumb ? (
        <div style={{ position: "relative", width, height }}>
          <img
            src={getProxyUrl(ad.thumb)}
            alt="Video thumb"
            style={style}
            onError={handleImageError}
          />
          <div
            style={{
              ...style,
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffebee",
              border: "1px dashed #f44336",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <i className="fas fa-video text-danger"></i>
          </div>
        </div>
      ) : (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#e8f4fd",
          }}
        >
          <i className="fas fa-video text-primary font-size-xs"></i>
        </div>
      );
    } else if (ad.mediatype === "eAdvertImage" || ad.mediatype === "eAdvertGif") {
      const src = ad.action || ad.thumb;
      previewElement = src ? (
        <div style={{ position: "relative", width, height }}>
          <img
            src={getProxyUrl(src)}
            alt="Ad media"
            style={style}
            onError={handleImageError}
          />
          <div
            style={{
              ...style,
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffebee",
              border: "1px dashed #f44336",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <i className="fas fa-image text-danger font-size-xs"></i>
          </div>
        </div>
      ) : (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#e8f4fd",
          }}
        >
          <i className="fas fa-image text-success font-size-xs"></i>
        </div>
      );
    } else {
      // Text Ad
      previewElement = (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#e8f4fd",
            fontSize: "11px",
            fontWeight: "bold",
            color: "#3699FF",
            padding: "2px",
            textAlign: "center",
          }}
        >
          Text
        </div>
      );
    }

    return previewElement;
  };

  // Business options for react-select in modal (dynamically filtered by currentAd.category_id)
  const businessOptions = useMemo(() => {
    let list = businesses;

    if (currentAd.category_id) {
      list = businesses.filter((b) =>
        businessBelongsToCategory(b, currentAd.category_id)
      );
    }

    const formattedList = list.map((b) => ({
      value: b.id,
      label: `${b.name} (ID: ${b.id})`,
      category_ids: extractBusinessCategoryIds(b),
    }));

    const selectedCategoryObj = currentAd.category_id
      ? categoriesWithBizList.find(
          (c) => c.id === Number(currentAd.category_id)
        ) || categories.find((c) => c.id === Number(currentAd.category_id))
      : null;

    const noneLabel = selectedCategoryObj
      ? `None / Platform Ad (General for ${selectedCategoryObj.name})`
      : "None / Platform Ad (General)";

    return [{ value: "", label: noneLabel }, ...formattedList];
  }, [businesses, currentAd.category_id, categoriesWithBizList, categories]);

  // Business options for table filter bar (filtered by filterCategory if selected)
  const filterBusinessOptions = useMemo(() => {
    let list = businesses;
    if (filterCategory) {
      list = businesses.filter((b) =>
        businessBelongsToCategory(b, filterCategory)
      );
    }
    return list;
  }, [businesses, filterCategory]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const singleSelectedIdx =
    selectedIds.length === 1 ? ads.findIndex((a) => a.id === selectedIds[0]) : -1;

  // Box statistics calculations
  const totalAdsAllBoxes = boxes.reduce((acc, b) => acc + (b.totalAds || 0), 0);
  const totalActiveAds = boxes.reduce((acc, b) => acc + (b.activeAds || 0), 0);

  return (
    <div>
      {/* Box Creation / Edit Modal */}
      <Modal show={showBoxModal} onHide={() => setShowBoxModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentBox.id
              ? `Edit Box ${currentBox.box_number}`
              : "Create New Advertisement Box / Space"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label className="font-weight-bold">
              Box Number (Slot Space) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              min="1"
              className={`form-control ${
                boxModalErrors.box_number ? "is-invalid" : ""
              }`}
              placeholder="e.g. 1, 2, 3..."
              value={currentBox.box_number}
              onChange={(e) =>
                setCurrentBox({ ...currentBox, box_number: e.target.value })
              }
            />
            {boxModalErrors.box_number && (
              <div className="invalid-feedback">{boxModalErrors.box_number}</div>
            )}
            <small className="form-text text-muted">
              Unique identifier for this advertising slot (e.g. Box 1, Box 2).
            </small>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              Box Name / Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${boxModalErrors.name ? "is-invalid" : ""}`}
              placeholder="e.g. Box 1 - Hero Top Banner Slot"
              value={currentBox.name}
              onChange={(e) =>
                setCurrentBox({ ...currentBox, name: e.target.value })
              }
            />
            {boxModalErrors.name && (
              <div className="invalid-feedback">{boxModalErrors.name}</div>
            )}
          </div>

          <div className="form-group">
            <label className="font-weight-bold">Description / Notes</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="e.g. Top banner displayed prominently on the mobile feed and homepage."
              value={currentBox.description}
              onChange={(e) =>
                setCurrentBox({ ...currentBox, description: e.target.value })
              }
            />
          </div>

          <div className="row">
            <div className="col-md-6 form-group">
              <label className="font-weight-bold">Status</label>
              <select
                className="form-control"
                value={currentBox.status}
                onChange={(e) =>
                  setCurrentBox({ ...currentBox, status: e.target.value })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-6 form-group">
              <label className="font-weight-bold">Display Order</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={currentBox.order}
                onChange={(e) =>
                  setCurrentBox({ ...currentBox, order: e.target.value })
                }
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowBoxModal(false)}
            disabled={savingBox}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveBox}
            disabled={savingBox}
          >
            {savingBox ? <i className="fas fa-circle-notch fa-spin mr-1"></i> : null}
            {currentBox.id ? "Save Changes" : "Create Box"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Advertisement Creation / Edit Modal */}
      <Modal
        show={showAdModal}
        onHide={() => setShowAdModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentAd.id ? "Edit Advertisement" : "New Advertisement"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Box Space Selection */}
            <div className="col-md-4 form-group">
              <label className="font-weight-bold">
                Assigned Box / Space <span className="text-danger">*</span>
              </label>
              <select
                className={`form-control ${
                  adModalErrors.box_id ? "is-invalid" : ""
                }`}
                value={currentAd.box_id}
                onChange={(e) => {
                  const bId = e.target.value;
                  const bObj = boxes.find((b) => b.id === Number(bId));
                  setCurrentAd({
                    ...currentAd,
                    box_id: bId,
                    ad_order: bObj ? bObj.box_number : 1,
                  });
                }}
              >
                <option value="">-- Select Box --</option>
                {boxes.map((b) => (
                  <option key={b.id} value={b.id}>
                    Box {b.box_number} ({b.name})
                  </option>
                ))}
              </select>
              {adModalErrors.box_id && (
                <div className="invalid-feedback">{adModalErrors.box_id}</div>
              )}
              <small className="form-text text-muted">
                Assign to a Box space where this ad will rotate.
              </small>
            </div>

            {/* Media Type */}
            <div className="col-md-4 form-group">
              <label className="font-weight-bold">Media Type</label>
              <select
                className="form-control"
                value={currentAd.mediatype}
                onChange={(e) =>
                  setCurrentAd({ ...currentAd, mediatype: e.target.value })
                }
              >
                <option value="eAdvertText">Text Advertisement</option>
                <option value="eAdvertImage">Image Advertisement</option>
                <option value="eAdvertVideo">Video Advertisement</option>
                <option value="eAdvertGif">GIF Advertisement</option>
              </select>
            </div>

            {/* Status */}
            <div className="col-md-4 form-group">
              <label className="font-weight-bold">Status</label>
              <select
                className="form-control"
                value={currentAd.status}
                onChange={(e) =>
                  setCurrentAd({ ...currentAd, status: e.target.value })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Category Selection: only display categories that have businesses */}
            <div className="col-md-6 form-group">
              <label className="font-weight-bold">
                Link to Category (Optional)
              </label>
              <select
                className="form-control"
                value={currentAd.category_id || ""}
                onChange={(e) => {
                  const newCatId = e.target.value;
                  let newBizId = currentAd.business_id;
                  if (newCatId && newBizId) {
                    const selectedBiz = businesses.find(
                      (b) => b.id === Number(newBizId)
                    );
                    if (
                      !selectedBiz ||
                      !businessBelongsToCategory(selectedBiz, newCatId)
                    ) {
                      newBizId = ""; // Reset business if it does not belong to the selected category
                    }
                  }
                  setCurrentAd({
                    ...currentAd,
                    category_id: newCatId,
                    business_id: newBizId,
                  });
                }}
              >
                <option value="">All Categories / General</option>
                {categoriesWithBizList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <small className="form-text text-muted">
                {categoriesWithBizList.length > 0
                  ? "Showing only categories that have registered businesses."
                  : "No categories with registered businesses found."}
              </small>
            </div>

            {/* Business Selection */}
            <div className="col-md-6 form-group">
              <label className="font-weight-bold">
                Link to Business (Optional)
              </label>
              <Select
                isClearable
                isSearchable
                placeholder={
                  currentAd.category_id
                    ? "Search & select business in this category..."
                    : "Search & select business..."
                }
                value={
                  businessOptions.find(
                    (opt) =>
                      opt.value ===
                      (currentAd.business_id ? Number(currentAd.business_id) : "")
                  ) || null
                }
                options={businessOptions}
                onChange={(selected) => {
                  const bId = selected ? selected.value : "";
                  let autoCatId = currentAd.category_id;
                  if (bId) {
                    const bObj = businesses.find((b) => b.id === Number(bId));
                    if (bObj) {
                      const bizCatIds = extractBusinessCategoryIds(bObj);
                      if (bizCatIds.length > 0) {
                        if (!autoCatId || !bizCatIds.includes(Number(autoCatId))) {
                          autoCatId = bizCatIds[0];
                        }
                      }
                    }
                  }
                  setCurrentAd({
                    ...currentAd,
                    business_id: bId,
                    category_id: autoCatId,
                  });
                }}
              />
              <small className="form-text text-muted">
                {currentAd.category_id ? (
                  businessOptions.length > 1 ? (
                    <span className="text-success font-weight-bold">
                      <i className="fas fa-check-circle mr-1 text-success"></i>
                      Found {businessOptions.length - 1} business(es) under this category.
                    </span>
                  ) : (
                    <span className="text-warning font-weight-bold">
                      <i className="fas fa-exclamation-triangle mr-1 text-warning"></i>
                      No businesses registered under this category yet.
                    </span>
                  )
                ) : (
                  "Link this advertisement to a registered business."
                )}
              </small>
            </div>

            {/* Default Advertisement Option */}
            <div className="col-md-12 form-group">
              <div
                className="p-3 rounded border d-flex align-items-center justify-content-between"
                style={{
                  backgroundColor: currentAd.is_default ? "#fff8dd" : "#f8f9fa",
                  borderColor: currentAd.is_default ? "#ffe885" : "#ebedf3",
                  transition: "all 0.2s ease",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: currentAd.is_default ? "#ffe885" : "#e4e6ef",
                      color: currentAd.is_default ? "#b58100" : "#7e8299",
                    }}
                  >
                    <i className="fas fa-star font-size-base"></i>
                  </div>
                  <div>
                    <div className="font-weight-bold text-dark font-size-base mb-1">
                      Default Advertisement Option
                    </div>
                    <div className="text-muted font-size-xs">
                      Mark as a default fallback ad for this box slot. When visitors browse without a category or no matching category ads exist, default ads are automatically displayed.
                    </div>
                  </div>
                </div>
                <div className="custom-control custom-switch custom-switch-lg ml-3 flex-shrink-0">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="is_default_switch"
                    checked={Boolean(currentAd.is_default)}
                    onChange={(e) =>
                      setCurrentAd({ ...currentAd, is_default: e.target.checked })
                    }
                  />
                  <label
                    className="custom-control-label font-weight-bolder"
                    htmlFor="is_default_switch"
                    style={{
                      cursor: "pointer",
                      color: currentAd.is_default ? "#b58100" : "#7e8299",
                    }}
                  >
                    {currentAd.is_default ? "Default Ad" : "Standard Ad"}
                  </label>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="col-md-12 form-group">
              <label className="font-weight-bold">Title (Optional)</label>
              <input
                className="form-control"
                type="text"
                placeholder="e.g. You see this..."
                value={currentAd.title}
                onChange={(e) =>
                  setCurrentAd({ ...currentAd, title: e.target.value })
                }
              />
            </div>

            {/* Detail */}
            <div className="col-md-12 form-group">
              <label className="font-weight-bold">Detail Description</label>
              <textarea
                className={`form-control ${
                  adModalErrors.detail ? "is-invalid" : ""
                }`}
                rows="3"
                placeholder="e.g. So will your customers"
                value={currentAd.detail}
                onChange={(e) =>
                  setCurrentAd({ ...currentAd, detail: e.target.value })
                }
              />
              {adModalErrors.detail && (
                <div className="invalid-feedback">{adModalErrors.detail}</div>
              )}
            </div>

            {/* Action / Destination */}
            <div className="col-md-12 form-group">
              <label className="font-weight-bold">
                Action (Target Link / Media URL / Button Label)
              </label>
              <div className="input-group">
                <input
                  className={`form-control ${
                    adModalErrors.action ? "is-invalid" : ""
                  }`}
                  type="text"
                  placeholder="e.g. https://.../video.mp4 or Advertise Here"
                  value={currentAd.action}
                  onChange={(e) =>
                    setCurrentAd({ ...currentAd, action: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    document.getElementById("action_file_input_modal").click()
                  }
                  disabled={uploadingAction}
                >
                  {uploadingAction ? (
                    <i className="fas fa-circle-notch fa-spin mr-1"></i>
                  ) : (
                    <i className="fas fa-upload mr-1"></i>
                  )}
                  Upload File
                </button>
              </div>
              <input
                id="action_file_input_modal"
                type="file"
                style={{ display: "none" }}
                onChange={(e) => handleActionFileUpload(e)}
                accept="image/*,video/*,image/gif"
              />
              {adModalErrors.action && (
                <div
                  className="text-danger mt-1"
                  style={{ fontSize: "0.85rem" }}
                >
                  {adModalErrors.action}
                </div>
              )}
            </div>

            {/* Thumb */}
            <div className="col-md-12 form-group">
              <label className="font-weight-bold">
                Thumbnail / Cover Image URL (Optional)
              </label>
              <div className="input-group">
                <input
                  className="form-control"
                  type="text"
                  placeholder="e.g. https://.../thumb.png"
                  value={currentAd.thumb || ""}
                  onChange={(e) =>
                    setCurrentAd({ ...currentAd, thumb: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    document.getElementById("thumb_file_input_modal").click()
                  }
                  disabled={uploadingThumb}
                >
                  {uploadingThumb ? (
                    <i className="fas fa-circle-notch fa-spin mr-1"></i>
                  ) : (
                    <i className="fas fa-upload mr-1"></i>
                  )}
                  Upload Image
                </button>
              </div>
              <input
                id="thumb_file_input_modal"
                type="file"
                style={{ display: "none" }}
                onChange={(e) => handleThumbFileUpload(e)}
                accept="image/*,image/gif"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowAdModal(false)}
            disabled={savingAd}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAd}
            disabled={savingAd}
          >
            {savingAd ? (
              <i className="fas fa-circle-notch fa-spin mr-1"></i>
            ) : null}
            Save Advertisement
          </button>
        </Modal.Footer>
      </Modal>

      {/* Main Container Card */}
      <Card>
        <CardHeader
          title={
            selectedBox
              ? `Box ${selectedBox.box_number}: ${selectedBox.name}`
              : "Advertisement Spaces & Boxes"
          }
        >
          <CardHeaderToolbar>
            {orderSaveMessage && (
              <span className="badge badge-light-success font-weight-bold mr-4 py-2 px-3">
                <i className="fas fa-check-circle text-success mr-1"></i>
                {orderSaveMessage}
              </span>
            )}
            {savingOrder && (
              <span className="badge badge-light-info font-weight-bold mr-4 py-2 px-3">
                <i className="fas fa-circle-notch fa-spin text-info mr-1"></i>
                Saving changes...
              </span>
            )}

            {selectedBox ? (
              <>
                <button
                  type="button"
                  className="btn btn-light-secondary font-weight-bold mr-2"
                  onClick={handleBackToBoxes}
                >
                  <i className="fas fa-arrow-left mr-1"></i> Back to All Boxes
                </button>
                {superadmin && (
                  <button
                    type="button"
                    className="btn btn-primary font-weight-bold"
                    onClick={() => handleOpenAddAdModal(selectedBox.id)}
                  >
                    <i className="fas fa-plus mr-1"></i> Add Ad to Box{" "}
                    {selectedBox.box_number}
                  </button>
                )}
              </>
            ) : (
              <>
                {superadmin && (
                  <>
                    <button
                      type="button"
                      className="btn btn-light-primary font-weight-bold mr-2"
                      onClick={handleOpenAddBoxModal}
                    >
                      <i className="fas fa-box-open mr-1"></i> New Box / Space
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary font-weight-bold"
                      onClick={() => handleOpenAddAdModal()}
                    >
                      <i className="fas fa-plus mr-1"></i> New Advertisement
                    </button>
                  </>
                )}
              </>
            )}
          </CardHeaderToolbar>
        </CardHeader>

        <CardBody>
          {/* Breadcrumb / Navigation Bar */}
          {!selectedBox && (
            <div className="d-flex justify-content-between align-items-center mb-6 pb-4 border-bottom">
              {/* Tab Switcher */}
              <ul className="nav nav-tabs nav-tabs-line nav-bold nav-tabs-line-2x border-0">
                <li className="nav-item">
                  <a
                    className={`nav-link ${
                      activeTab === "boxes" ? "active" : ""
                    }`}
                    href="#boxes"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("boxes");
                    }}
                    style={{ cursor: "pointer", fontSize: "1rem" }}
                  >
                    <i className="fas fa-boxes mr-2"></i>
                    Boxes / Spaces ({boxes.length})
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${
                      activeTab === "all-ads" ? "active" : ""
                    }`}
                    href="#all-ads"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("all-ads");
                    }}
                    style={{ cursor: "pointer", fontSize: "1rem" }}
                  >
                    <i className="fas fa-layer-group mr-2"></i>
                    All Advertisements ({totalAdsAllBoxes})
                  </a>
                </li>
              </ul>

              {/* Summary Stats Chips */}
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                <span className="label label-light-primary label-inline font-weight-bolder py-3 px-3 font-size-sm">
                  <i className="fas fa-boxes text-primary mr-2"></i>
                  {boxes.length} Boxes
                </span>
                <span className="label label-light-success label-inline font-weight-bolder py-3 px-3 font-size-sm">
                  <i className="fas fa-check-circle text-success mr-2"></i>
                  {totalActiveAds} Active Ads
                </span>
                <span className="label label-light-info label-inline font-weight-bolder py-3 px-3 font-size-sm">
                  <i className="fas fa-layer-group text-info mr-2"></i>
                  {totalAdsAllBoxes} Total Ads
                </span>
              </div>
            </div>
          )}

          {/* VIEW 1: TOP-LEVEL BOXES / SPACES WITH CROSS-BOX DRAG AND DROP */}
          {!selectedBox && activeTab === "boxes" && (
            <div>
              <div
                className="alert alert-custom alert-light-primary fade show mb-6 py-3"
                role="alert"
              >
                <div className="alert-icon">
                  <i className="fas fa-hand-pointer text-primary"></i>
                </div>
                <div className="alert-text font-size-sm">
                  <strong>Interactive Drag & Drop:</strong> You can drag any
                  advertisement item directly from one Box card and drop it onto
                  another Box card to immediately re-assign it. Or click{" "}
                  <strong>"Manage Ads"</strong> to inspect and organize a specific
                  box.
                </div>
              </div>

              {boxesLoading ? (
                <div className="text-center py-10 text-muted">
                  <i className="fas fa-circle-notch fa-spin fa-2x mr-2"></i>{" "}
                  Loading Boxes...
                </div>
              ) : boxes.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-muted mb-4">No advertisement boxes found.</p>
                  {superadmin && (
                    <button
                      type="button"
                      className="btn btn-primary font-weight-bold"
                      onClick={handleOpenAddBoxModal}
                    >
                      <i className="fas fa-plus mr-1"></i> Create First Box
                    </button>
                  )}
                </div>
              ) : (
                <div className="row">
                  {boxes.map((b) => {
                    const isDragOverThisBox = dragOverBoxId === b.id;
                    const boxAdsList = (b.advertisements && b.advertisements.length > 0) ? b.advertisements : (b.previewAds || []);

                    return (
                      <div
                        className="col-xl-6 col-lg-6 col-md-12 mb-6"
                        key={b.id}
                      >
                        <div
                          className="card card-custom card-stretch gutter-b"
                          onDragOver={(e) => handleBoxDragOver(e, b.id)}
                          onDragLeave={(e) => handleBoxDragLeave(e, b.id)}
                          onDrop={(e) => handleDropOnBox(e, b)}
                          style={{
                            border: isDragOverThisBox
                              ? "2px dashed #3699FF"
                              : "1px solid #EBEDF3",
                            backgroundColor: isDragOverThisBox
                              ? "#f0f8ff"
                              : "#ffffff",
                            boxShadow: isDragOverThisBox
                              ? "0 0 25px rgba(54, 153, 255, 0.25)"
                              : "0px 0px 20px 0px rgba(76, 87, 125, 0.04)",
                            borderRadius: "10px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {/* Card Header with Fixed Clean Alignment */}
                          <div
                            className="card-header border-0 pt-6 px-6 pb-2"
                            style={{ minHeight: "auto" }}
                          >
                            <div className="d-flex flex-wrap align-items-start justify-content-between w-100">
                              <div
                                className="d-flex align-items-center mb-2"
                                style={{ gap: "12px", maxWidth: "75%" }}
                              >
                                <span
                                  className="badge badge-primary font-weight-bolder"
                                  style={{
                                    backgroundColor: "#3699FF",
                                    color: "#fff",
                                    fontSize: "0.9rem",
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    flexShrink: 0,
                                    boxShadow:
                                      "0 2px 4px rgba(54, 153, 255, 0.25)",
                                  }}
                                >
                                  Box {b.box_number}
                                </span>
                                <div>
                                  <h4
                                    className="card-label font-weight-bolder text-dark mb-1"
                                    style={{
                                      fontSize: "1.15rem",
                                      lineHeight: "1.3",
                                    }}
                                  >
                                    {b.name}
                                  </h4>
                                  <span className="text-muted font-size-sm">
                                    Slot #{b.box_number} • Display Order:{" "}
                                    {b.order || b.box_number}
                                  </span>
                                </div>
                              </div>
                              <div className="mb-2">
                                <span
                                  className={`label label-inline font-weight-bold ${
                                    b.status === "active"
                                      ? "label-light-success"
                                      : "label-light-danger"
                                  }`}
                                  style={{
                                    cursor: superadmin ? "pointer" : "default",
                                    padding: "6px 10px",
                                  }}
                                  onClick={() =>
                                    superadmin &&
                                    toggleBoxStatus(b.id, b.status)
                                  }
                                  title={
                                    superadmin
                                      ? "Click to toggle box status"
                                      : undefined
                                  }
                                >
                                  {b.status === "active"
                                    ? "Active Space"
                                    : "Inactive"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="card-body px-6 pt-2 pb-5">
                            {b.description && (
                              <p className="text-dark-50 font-size-sm mb-4">
                                {b.description}
                              </p>
                            )}

                            {/* Drop Zone Highlight Banner */}
                            {isDragOverThisBox && (
                              <div
                                className="alert alert-custom alert-light-primary py-2 px-3 mb-4 text-center font-weight-bolder"
                                style={{
                                  border: "1px dashed #3699FF",
                                  borderRadius: "6px",
                                }}
                              >
                                <i className="fas fa-arrow-circle-down text-primary mr-2"></i>
                                Drop here to move ad to Box {b.box_number} (
                                {b.name})
                              </div>
                            )}

                            {/* Metrics Row */}
                            <div
                              className="d-flex align-items-center justify-content-between rounded p-3 mb-4"
                              style={{
                                backgroundColor: "#f7f9fb",
                                border: "1px solid #edf0f5",
                              }}
                            >
                              <div className="text-center flex-grow-1 border-right">
                                <span className="text-muted font-size-xs text-uppercase font-weight-bolder">
                                  TOTAL ADS
                                </span>
                                <div className="font-size-h5 font-weight-boldest text-dark mt-1">
                                  {b.totalAds || 0}
                                </div>
                              </div>
                              <div className="text-center flex-grow-1 border-right">
                                <span className="text-muted font-size-xs text-uppercase font-weight-bolder">
                                  ACTIVE ADS
                                </span>
                                <div className="font-size-h5 font-weight-boldest text-success mt-1">
                                  {b.activeAds || 0}
                                </div>
                              </div>
                              <div className="text-center flex-grow-1">
                                <span className="text-muted font-size-xs text-uppercase font-weight-bolder">
                                  INACTIVE
                                </span>
                                <div className="font-size-h5 font-weight-boldest text-danger mt-1">
                                  {b.inactiveAds || 0}
                                </div>
                              </div>
                            </div>

                            {/* Ads Inside this Box with Drag Handle */}
                            <div className="mb-4">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="text-muted font-size-xs text-uppercase font-weight-bolder">
                                  ADS ROTATING IN THIS BOX ({boxAdsList.length}):
                                </span>
                                <span
                                  className="text-muted font-size-xs"
                                  style={{ fontStyle: "italic" }}
                                >
                                  Drag ad card to move to another Box
                                </span>
                              </div>

                              {boxAdsList.length === 0 ? (
                                <div
                                  className="text-center p-4 rounded"
                                  style={{
                                    backgroundColor: "#fafbfd",
                                    border: "1px dashed #dbe0ea",
                                    color: "#8898aa",
                                  }}
                                >
                                  <i className="fas fa-inbox fa-2x mb-2 d-block text-muted"></i>
                                  <span className="font-size-sm">
                                    No ads in this box yet.
                                  </span>
                                  {superadmin && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-light-primary font-weight-bold"
                                        onClick={() =>
                                          handleOpenAddAdModal(b.id)
                                        }
                                      >
                                        <i className="fas fa-plus mr-1"></i> Add
                                        First Ad
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                    paddingRight: "4px",
                                  }}
                                >
                                  {boxAdsList.map((ad, idx) => (
                                    <div
                                      key={ad.id}
                                      draggable={superadmin && !savingOrder}
                                      onDragStart={(e) =>
                                        handleAdDragStart(e, ad, b.id, idx)
                                      }
                                      onDragEnd={handleAdDragEnd}
                                      className="d-flex align-items-center justify-content-between p-2 mb-2 rounded bg-white"
                                      style={{
                                        border: "1px solid #e2e5ec",
                                        boxShadow:
                                          "0 1px 3px rgba(0, 0, 0, 0.05)",
                                        cursor: superadmin
                                          ? "grab"
                                          : "default",
                                        transition: "all 0.15s ease",
                                      }}
                                      title={
                                        superadmin
                                          ? "Drag this ad to another Box card"
                                          : undefined
                                      }
                                    >
                                      {/* Left: Drag grip & Preview */}
                                      <div
                                        className="d-flex align-items-center"
                                        style={{ gap: "8px", minWidth: 0 }}
                                      >
                                        {superadmin && (
                                          <i
                                            className="fas fa-grip-vertical text-muted mr-1"
                                            style={{
                                              cursor: "grab",
                                              userSelect: "none",
                                            }}
                                          ></i>
                                        )}
                                        {renderMediaPreview(ad, true)}
                                        <div
                                          style={{
                                            minWidth: 0,
                                            maxWidth: "200px",
                                          }}
                                        >
                                          <div
                                            className="font-weight-bold text-dark text-truncate"
                                            style={{ fontSize: "0.85rem" }}
                                            title={ad.title || `Ad #${ad.id}`}
                                          >
                                            {ad.title || (
                                              <span className="text-muted">
                                                Ad #{ad.id}
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            className="text-muted font-size-xs text-truncate"
                                            title={ad.detail}
                                          >
                                            {ad.detail ||
                                              ad.action ||
                                              ad.mediatype}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Right: Badges & Actions */}
                                      <div
                                        className="d-flex align-items-center ml-2"
                                        style={{ gap: "6px", flexShrink: 0 }}
                                      >
                                        {ad.is_default && (
                                          <span
                                            className="badge font-weight-bolder"
                                            style={{
                                              fontSize: "0.72rem",
                                              backgroundColor: "#fff8dd",
                                              color: "#b58100",
                                              border: "1px solid #ffe885",
                                            }}
                                            title="Default Advertisement"
                                          >
                                            <i className="fas fa-star mr-1 font-size-xs text-warning"></i>Default
                                          </span>
                                        )}
                                        <span
                                          className="badge badge-light font-weight-bold"
                                          style={{ fontSize: "0.75rem" }}
                                        >
                                          {ad.mediatype.replace("eAdvert", "")}
                                        </span>
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            superadmin &&
                                              toggleAdStatus(ad.id, ad.status);
                                          }}
                                          className={`badge font-weight-bold ${
                                            ad.status === "active"
                                              ? "badge-light-success"
                                              : "badge-light-danger"
                                          }`}
                                          style={{
                                            cursor: superadmin
                                              ? "pointer"
                                              : "default",
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {ad.status === "active"
                                            ? "Active"
                                            : "Inactive"}
                                        </span>

                                        {superadmin && (
                                          <div className="d-flex align-items-center">
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-xs btn-light btn-hover-primary mx-1"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenEditAdModal(ad);
                                              }}
                                              title="Edit Ad"
                                            >
                                              <i className="fas fa-edit font-size-xs text-primary"></i>
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-xs btn-light btn-hover-danger"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteAd(ad.id);
                                              }}
                                              title="Delete Ad"
                                            >
                                              <i className="fas fa-trash font-size-xs text-danger"></i>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Card Footer Actions */}
                            <div className="d-flex flex-wrap align-items-center justify-content-between pt-3 border-top">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm font-weight-bold mb-1"
                                onClick={() => handleSelectBox(b)}
                              >
                                <i className="fas fa-external-link-alt mr-1"></i>{" "}
                                Manage Ads ({b.totalAds || 0})
                              </button>

                              {superadmin && (
                                <div className="d-flex align-items-center mb-1">
                                  <button
                                    type="button"
                                    className="btn btn-light-success btn-sm font-weight-bold mr-2"
                                    onClick={() => handleOpenAddAdModal(b.id)}
                                    title="Add new ad into this Box"
                                  >
                                    <i className="fas fa-plus mr-1"></i> Add Ad
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-light btn-hover-primary btn-sm mr-2"
                                    onClick={() => handleOpenEditBoxModal(b)}
                                    title="Edit Box"
                                  >
                                    <i className="fas fa-cog text-primary"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-light btn-hover-danger btn-sm"
                                    onClick={() => handleDeleteBox(b)}
                                    title="Delete Box"
                                  >
                                    <i className="fas fa-trash text-danger"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* VIEW 2: DRILLDOWN (Inside a Selected Box) OR ALL ADS TAB */}
          {(selectedBox || activeTab === "all-ads") && (
            <div>
              {/* If in Drilldown View, show Box Header Banner */}
              {selectedBox && (
                <div
                  className="d-flex align-items-center justify-content-between p-4 mb-6 rounded"
                  style={{
                    backgroundColor: "#f3f6f9",
                    borderLeft: "4px solid #3699FF",
                  }}
                >
                  <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                    <span
                      className="badge font-weight-bolder"
                      style={{
                        backgroundColor: "#3699FF",
                        color: "#fff",
                        fontSize: "1rem",
                        padding: "6px 12px",
                      }}
                    >
                      Box {selectedBox.box_number}
                    </span>
                    <div>
                      <h4 className="font-weight-bolder text-dark mb-0">
                        {selectedBox.name}
                      </h4>
                      {selectedBox.description && (
                        <p className="text-muted font-size-sm mt-1 mb-0">
                          {selectedBox.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge badge-light-primary font-weight-bold mr-3 py-2 px-3">
                      {totalCount} Ads in this Box
                    </span>
                    {superadmin && (
                      <button
                        type="button"
                        className="btn btn-light-primary btn-sm font-weight-bold mr-2"
                        onClick={() => handleOpenEditBoxModal(selectedBox)}
                      >
                        <i className="fas fa-cog mr-1"></i> Box Settings
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Filters Form - Clean 2-Row Design */}
              <div className="card card-custom bg-white border gutter-b mb-6 shadow-xs">
                <div className="card-body p-5">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setPageNumber(1);
                      fetchAds();
                    }}
                  >
                    {/* Top Row: Search Input + Apply & Reset Buttons */}
                    <div className="d-flex align-items-center flex-wrap mb-4" style={{ gap: "10px" }}>
                      <div className="flex-grow-1" style={{ minWidth: "240px" }}>
                        <div className="input-icon">
                          <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder="Search by Title or Keywords..."
                            value={searchTitle}
                            onChange={(e) => setSearchTitle(e.target.value)}
                          />
                          <span>
                            <i className="fas fa-search text-muted"></i>
                          </span>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary font-weight-bold px-5"
                      >
                        <i className="fas fa-search mr-2 font-size-xs"></i> Search
                      </button>
                      <button
                        type="button"
                        className="btn btn-light-danger font-weight-bold px-4"
                        title="Reset all filters"
                        onClick={() => {
                          setSearchTitle("");
                          setSearchDetail("");
                          setFilterBox("");
                          setFilterCategory("");
                          setFilterBusiness("");
                          setFilterMediaType("");
                          setFilterStatus("");
                          setFilterDefault("");
                          setPageNumber(1);
                        }}
                      >
                        <i className="fas fa-undo mr-1 font-size-xs"></i> Reset
                      </button>
                    </div>

                    {/* Bottom Row: Aligned Filter Selectors */}
                    <div className="row">
                      {!selectedBox && (
                        <div className="col-lg col-md-4 col-sm-6 mb-2">
                          <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                            Box Space
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={filterBox}
                            onChange={(e) => {
                              setFilterBox(e.target.value);
                              setPageNumber(1);
                            }}
                          >
                            <option value="">All Boxes</option>
                            {boxes.map((b) => (
                              <option key={b.id} value={b.id}>
                                Box {b.box_number} ({b.name})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="col-lg col-md-4 col-sm-6 mb-2">
                        <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                          Category
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={filterCategory}
                          onChange={(e) => {
                            setFilterCategory(e.target.value);
                            setFilterBusiness("");
                            setPageNumber(1);
                          }}
                        >
                          <option value="">All Categories</option>
                          {categoriesWithBizList.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg col-md-4 col-sm-6 mb-2">
                        <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                          Business
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={filterBusiness}
                          onChange={(e) => {
                            setFilterBusiness(e.target.value);
                            setPageNumber(1);
                          }}
                        >
                          <option value="">All Businesses</option>
                          {filterBusinessOptions.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg col-md-4 col-sm-6 mb-2">
                        <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                          Media Type
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={filterMediaType}
                          onChange={(e) => {
                            setFilterMediaType(e.target.value);
                            setPageNumber(1);
                          }}
                        >
                          <option value="">All Media Types</option>
                          <option value="eAdvertText">Text</option>
                          <option value="eAdvertImage">Image</option>
                          <option value="eAdvertVideo">Video</option>
                          <option value="eAdvertGif">GIF</option>
                        </select>
                      </div>

                      <div className="col-lg col-md-4 col-sm-6 mb-2">
                        <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                          Status
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={filterStatus}
                          onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setPageNumber(1);
                          }}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div className="col-lg col-md-4 col-sm-6 mb-2">
                        <label className="text-muted font-size-xs text-uppercase font-weight-bolder mb-1 d-block">
                          Ad Type
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={filterDefault}
                          onChange={(e) => {
                            setFilterDefault(e.target.value);
                            setPageNumber(1);
                          }}
                        >
                          <option value="">All Types</option>
                          <option value="default">⭐ Default Ads Only</option>
                          <option value="standard">Standard Ads</option>
                        </select>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Bulk Actions Alert */}
              {selectedIds.length > 0 && superadmin && (
                <div
                  className="alert alert-custom alert-light-primary fade show mb-5 py-3"
                  role="alert"
                >
                  <div className="alert-text font-weight-bold">
                    {selectedIds.length} advertisements selected
                  </div>
                  <div className="alert-toolbar">
                    <button
                      type="button"
                      className="btn btn-light-primary btn-sm font-weight-bold mr-2"
                      onClick={handleBulkAssignBox}
                      title="Move selected advertisements to another Box"
                    >
                      <i className="fas fa-boxes mr-1"></i> Move to Box...
                    </button>
                    {singleSelectedIdx !== -1 && (
                      <>
                        <button
                          type="button"
                          className="btn btn-light-primary btn-sm font-weight-bold mr-2"
                          disabled={singleSelectedIdx === 0 || savingOrder}
                          onClick={() =>
                            handleTableMove(
                              singleSelectedIdx,
                              singleSelectedIdx - 1
                            )
                          }
                        >
                          <i className="fas fa-arrow-up mr-1"></i> Move Up
                        </button>
                        <button
                          type="button"
                          className="btn btn-light-primary btn-sm font-weight-bold mr-2"
                          disabled={
                            singleSelectedIdx === ads.length - 1 || savingOrder
                          }
                          onClick={() =>
                            handleTableMove(
                              singleSelectedIdx,
                              singleSelectedIdx + 1
                            )
                          }
                        >
                          <i className="fas fa-arrow-down mr-1"></i> Move Down
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn btn-light-danger btn-sm font-weight-bold mr-2"
                      onClick={handleBulkDeleteAds}
                    >
                      Bulk Delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-light-success btn-sm font-weight-bold mr-2"
                      onClick={() => handleBulkStatusAds("active")}
                    >
                      Bulk Activate
                    </button>
                    <button
                      type="button"
                      className="btn btn-light-warning btn-sm font-weight-bold"
                      onClick={() => handleBulkStatusAds("inactive")}
                    >
                      Bulk Deactivate
                    </button>
                  </div>
                </div>
              )}

              {/* Advertisements Table - Clean & Spacious */}
              <div className="table-responsive">
                <table className="table table-head-custom table-vertical-center overflow-hidden">
                  <thead>
                    <tr className="text-left text-uppercase font-weight-bold">
                      {superadmin && (
                        <th style={{ width: "35px" }} className="pl-0">
                          <label className="checkbox checkbox-single checkbox-primary checkbox-outline">
                            <input
                              type="checkbox"
                              checked={
                                selectedIds.length === ads.length &&
                                ads.length > 0
                              }
                              onChange={handleSelectAll}
                            />
                            <span
                              style={{
                                border: "1.5px solid #B5B5C3",
                                borderRadius: "3px",
                                backgroundColor: "#fff",
                              }}
                            ></span>
                          </label>
                        </th>
                      )}
                      <th style={{ width: "100px" }}>Slot</th>
                      <th style={{ minWidth: "260px" }}>Advertisement</th>
                      <th style={{ minWidth: "170px" }}>Association</th>
                      <th style={{ width: "90px" }}>Media</th>
                      <th style={{ minWidth: "120px" }}>Destination</th>
                      <th style={{ width: "90px" }}>Status</th>
                      {superadmin && <th className="text-right" style={{ width: "100px" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {adsLoading ? (
                      <tr>
                        <td
                          colSpan={superadmin ? 8 : 7}
                          className="text-center py-10 text-muted"
                        >
                          <i className="fas fa-circle-notch fa-spin fa-2x mr-2"></i>
                          Loading advertisements...
                        </td>
                      </tr>
                    ) : ads.length === 0 ? (
                      <tr>
                        <td
                          colSpan={superadmin ? 8 : 7}
                          className="text-center py-10 text-muted"
                        >
                          No advertisements found in this view.
                          {superadmin && selectedBox && (
                            <div className="mt-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary font-weight-bold"
                                onClick={() =>
                                  handleOpenAddAdModal(selectedBox.id)
                                }
                              >
                                <i className="fas fa-plus mr-1"></i> Add
                                Advertisement to Box {selectedBox.box_number}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      ads.map((ad, index) => {
                        const boxName = ad.box
                          ? ad.box.name
                          : `Box ${ad.ad_order || 1}`;
                        const boxNum = ad.box
                          ? ad.box.box_number
                          : ad.ad_order || 1;

                        return (
                          <tr
                            key={ad.id}
                            draggable={superadmin && !adsLoading && !savingOrder}
                            onDragStart={(e) =>
                              handleAdDragStart(
                                e,
                                ad,
                                ad.box_id || (ad.box ? ad.box.id : null),
                                index
                              )
                            }
                            onDragEnd={handleAdDragEnd}
                            style={{
                              opacity:
                                ad.status === "inactive" ? 0.65 : 1,
                              cursor: superadmin ? "grab" : "default",
                            }}
                            title={
                              superadmin
                                ? "Drag row to move ad between boxes or re-order"
                                : undefined
                            }
                          >
                            {superadmin && (
                              <td
                                className="pl-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <label className="checkbox checkbox-single checkbox-primary checkbox-outline">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(ad.id)}
                                    onChange={(e) =>
                                      handleSelectRow(ad.id, e.target.checked)
                                    }
                                  />
                                  <span
                                    style={{
                                      border: "1.5px solid #B5B5C3",
                                      borderRadius: "3px",
                                      backgroundColor: "#fff",
                                    }}
                                  ></span>
                                </label>
                              </td>
                            )}

                            {/* Box Slot Badge */}
                            <td>
                              <div className="d-flex align-items-center">
                                {superadmin && (
                                  <span
                                    className="text-muted mr-2"
                                    style={{
                                      cursor: "grab",
                                      userSelect: "none",
                                    }}
                                    title="Drag to move"
                                  >
                                    <i className="fas fa-grip-vertical"></i>
                                  </span>
                                )}
                                <span
                                  className="label label-primary label-inline font-weight-bolder"
                                  style={{ minWidth: "55px" }}
                                  title={boxName}
                                >
                                  Box {boxNum}
                                </span>
                              </div>
                            </td>

                            {/* Combined Thumbnail + Title + Details */}
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="mr-3 flex-shrink-0">
                                  {renderMediaPreview(ad, true)}
                                </div>
                                <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                                  <div className="d-flex align-items-center flex-wrap">
                                    <span className="font-weight-bolder text-dark-75 text-hover-primary text-truncate mr-2 font-size-base">
                                      {ad.title || "Untitled Ad"}
                                    </span>
                                    <span className="label label-light-secondary label-inline text-muted font-weight-bold font-size-xs mr-2">
                                      #{ad.id}
                                    </span>
                                    {ad.is_default ? (
                                      <span
                                        className="label font-weight-bolder font-size-xs"
                                        title={superadmin ? "Default Advertisement - Click to toggle to standard" : "Default Advertisement"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          superadmin && toggleAdDefault(ad.id, ad.is_default);
                                        }}
                                        style={{
                                          cursor: superadmin ? "pointer" : "default",
                                          backgroundColor: "#fff8dd",
                                          color: "#b58100",
                                          border: "1px solid #ffe885",
                                        }}
                                      >
                                        <i className="fas fa-star mr-1 font-size-xs text-warning"></i>Default
                                      </span>
                                    ) : superadmin ? (
                                      <span
                                        className="label label-light-secondary label-inline text-muted font-size-xs"
                                        title="Click to set as default advertisement"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleAdDefault(ad.id, ad.is_default);
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        Standard
                                      </span>
                                    ) : (
                                      <span className="label label-light-secondary label-inline text-muted font-size-xs">
                                        Standard
                                      </span>
                                    )}
                                  </div>
                                  {ad.detail && (
                                    <span
                                      className="text-muted font-size-xs mt-1 text-truncate"
                                      style={{ maxWidth: "280px" }}
                                      title={ad.detail}
                                    >
                                      {ad.detail}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Association (Category & Business) */}
                            <td>
                              <div className="d-flex flex-column" style={{ gap: "3px" }}>
                                <div className="text-dark-75 font-weight-bold font-size-sm d-flex align-items-center text-truncate" style={{ maxWidth: "160px" }} title={`Category: ${ad.category ? ad.category.name : "All Categories"}`}>
                                  <i className="fas fa-tag text-primary mr-2 font-size-xs flex-shrink-0"></i>
                                  <span className="text-truncate">{ad.category ? ad.category.name : "All Categories"}</span>
                                </div>
                                <div className="text-muted font-size-xs d-flex align-items-center text-truncate" style={{ maxWidth: "160px" }} title={`Business: ${ad.business ? ad.business.name : "General / Platform"}`}>
                                  <i className="fas fa-store text-muted mr-2 font-size-xs flex-shrink-0"></i>
                                  <span className="text-truncate">{ad.business ? ad.business.name : "General / Platform"}</span>
                                </div>
                              </div>
                            </td>

                            {/* Media Type */}
                            <td>
                              <span className="label label-light-secondary label-inline font-weight-bold text-dark-75">
                                {ad.mediatype ? ad.mediatype.replace("eAdvert", "") : "Text"}
                              </span>
                            </td>

                            {/* Destination */}
                            <td>
                              <div
                                style={{
                                  maxWidth: "130px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={ad.action}
                              >
                                {ad.action && ad.action.startsWith("http") ? (
                                  <a
                                    href={ad.action}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-xs btn-light-primary font-weight-bold"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="fas fa-external-link-alt mr-1 font-size-xs"></i>
                                    Link
                                  </a>
                                ) : (
                                  <span className="text-muted font-size-sm">{ad.action || "-"}</span>
                                )}
                              </div>
                            </td>

                            {/* Status */}
                            <td>
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  superadmin &&
                                    toggleAdStatus(ad.id, ad.status);
                                }}
                                style={{
                                  cursor: superadmin
                                    ? "pointer"
                                    : "default",
                                }}
                                className={`label label-inline font-weight-bolder ${
                                  ad.status === "active"
                                    ? "label-light-success text-success"
                                    : "label-light-danger text-danger"
                                }`}
                              >
                                {ad.status === "active"
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                            </td>

                            {/* Actions - Horizontal side-by-side */}
                            {superadmin && (
                              <td
                                className="text-right pr-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="d-inline-flex align-items-center justify-content-end">
                                  <button
                                    title="Edit advertisement"
                                    className="btn btn-icon btn-light btn-hover-primary btn-sm mx-1"
                                    onClick={() => handleOpenEditAdModal(ad)}
                                  >
                                    <i className="fas fa-edit text-primary font-size-xs"></i>
                                  </button>
                                  <button
                                    title="Delete advertisement"
                                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-1"
                                    onClick={() => handleDeleteAd(ad.id)}
                                  >
                                    <i className="fas fa-trash text-danger font-size-xs"></i>
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center flex-wrap mt-5">
                <div className="d-flex align-items-center py-3">
                  <span className="text-muted mr-3">
                    Showing {ads.length} of {totalCount} records
                  </span>
                  <select
                    className="form-control form-control-sm font-weight-bold mr-4"
                    style={{ width: "75px" }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPageNumber(1);
                    }}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                <div className="d-flex flex-wrap py-2">
                  <button
                    className="btn btn-icon btn-sm btn-light btn-hover-primary mr-2"
                    disabled={pageNumber === 1}
                    onClick={() => setPageNumber(1)}
                  >
                    <i className="ki ki-bold-double-arrow-back icon-xs"></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light btn-hover-primary mr-2"
                    disabled={pageNumber === 1}
                    onClick={() => setPageNumber(pageNumber - 1)}
                  >
                    <i className="ki ki-bold-arrow-back icon-xs"></i>
                  </button>

                  <span className="btn btn-sm btn-light btn-hover-primary active mr-2">
                    Page {pageNumber} of {totalPages}
                  </span>

                  <button
                    className="btn btn-icon btn-sm btn-light btn-hover-primary mr-2"
                    disabled={pageNumber === totalPages}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  >
                    <i className="ki ki-bold-arrow-next icon-xs"></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light btn-hover-primary"
                    disabled={pageNumber === totalPages}
                    onClick={() => setPageNumber(totalPages)}
                  >
                    <i className="ki ki-bold-double-arrow-next icon-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
